import { useForm } from 'antd/es/form/Form';
import { useCallback, useContext, useState } from 'react';

import {
  useSearchEmployeeSalaryEmployeesMutation,
  useUpdateEmployeeSalaryMutation,
  type ModelEmployeeSalarySearchResult,
} from '@app/api/EmployeeSalary';
import {
  CONFIRM_SALARY,
  EDIT_AFTER_CONFIRM,
  EDIT_BEFORE_CONFIRM,
  RIGHTS_SALARIES,
  SALARY_REPORT,
  SUCCESS,
  SUCCESS_MESSAGE,
} from '@app/constants';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { DEFAULT_FILTERS_DATA } from '@app/pages/Analytics/data';
import { useShownReportPeriod } from '@app/pages/Analytics/hooks';
import type {
  ColumnDataType,
  FiltersInterface,
} from '@app/pages/Analytics/SalaryReport/types';
import { READ_ONLY_SALARY_FIELDS } from '@app/pages/Analytics/SalaryReport/utils';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useSalaryReport = () => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<ColumnDataType | null>(null);
  const [editableField, setEditableField] = useState<string | null>(null);
  const [editCellModalOpen, setEditCellModalOpen] = useState(false);
  const [editableValue, setEditableValue] = useState<string | null>(null);
  const [tableData, setTableData] = useState<
    ModelEmployeeSalarySearchResult | undefined
  >(undefined);
  const [isRevokeConfirm, setIsRevokeConfirm] = useState(false);

  const { showNotification } = useContext(NotificationContext);
  // Подтверждение, отмена подтверждения и правка ячейки относятся к периоду
  // показанной таблицы, а не к текущим (возможно, не применённым) селектам.
  const { shownPeriod, setShownPeriod } = useShownReportPeriod(SALARY_REPORT);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [form] = useForm<FiltersInterface>();

  const [updateEmployeeSalary, { isLoading: isUpdateEmployeeSalaryLoading }] =
    useUpdateEmployeeSalaryMutation();
  const [searchEmployees, { isLoading }] =
    useSearchEmployeeSalaryEmployeesMutation();

  const { hasUserRight: hasUserRightToEditBefore } = useAccessRights(
    RIGHTS_SALARIES,
    EDIT_BEFORE_CONFIRM
  );
  const { hasUserRight: hasUserRightToEditAfterConfirm } = useAccessRights(
    RIGHTS_SALARIES,
    EDIT_AFTER_CONFIRM
  );
  const { hasUserRight: hasUserRightToConfirmSalary } = useAccessRights(
    RIGHTS_SALARIES,
    CONFIRM_SALARY
  );

  const handleSearchData = useCallback(
    async ({ filterMonth, filterYear }: FiltersInterface) => {
      try {
        const result = await searchEmployees({
          data: {
            Month: filterMonth,
            Year: filterYear,
          },
          ActiveBranchId,
        }).unwrap();

        setTableData(result);
        setShownPeriod({ filterMonth, filterYear });
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, searchEmployees, setShownPeriod, showNotification]
  );

  // Перезагрузка таблицы после изменений — за показанный период.
  const fetchData = useCallback(
    () => handleSearchData(shownPeriod),
    [handleSearchData, shownPeriod]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    handleSearchData(DEFAULT_FILTERS_DATA);
  }, [form, handleSearchData]);

  const handleSubmitForm = useCallback(
    (values: FiltersInterface) => {
      const { filterMonth, filterYear } = values;

      handleSearchData({
        filterMonth,
        filterYear,
      });
    },
    [handleSearchData]
  );

  const onConfirmSalary = useCallback((userId: number) => {
    setIsRevokeConfirm(false);
    setSelectedEmployeeId(userId);
    setIsConfirmModalOpen(true);
  }, []);

  const handleEditableCellClick = useCallback(
    (record: ColumnDataType, field: string) => {
      if (READ_ONLY_SALARY_FIELDS.has(field)) return;

      const isRecordConfirmed = record.IsConfirmed;
      if (isRecordConfirmed && !hasUserRightToEditAfterConfirm) return;
      if (!isRecordConfirmed && !hasUserRightToEditBefore) return;

      const editableAmount = record[field];
      if (typeof editableAmount === 'boolean') return;

      setEditingCell(record);
      setEditableField(field);
      setEditableValue(String(editableAmount ?? ''));
      setEditCellModalOpen(true);
    },
    [hasUserRightToEditAfterConfirm, hasUserRightToEditBefore]
  );

  const closeEditCellModal = useCallback(() => {
    setEditingCell(null);
    setEditableField(null);
    setEditCellModalOpen(false);
    setEditableValue(null);
  }, []);

  const handleEditableValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setEditableValue(value);
    },
    []
  );

  const handleSaveEditCellModal = useCallback(async () => {
    if (!editingCell || !editableField) return;

    const { filterMonth, filterYear } = shownPeriod;

    const getValue = (field: string, current: number) =>
      editableField === field ? Number(editableValue ?? 0) : current;

    const comment =
      editableField === 'Comment' ? editableValue : editingCell.Comment;

    try {
      const data = {
        UserId: editingCell.UserId,
        AdvanceAmount: getValue('AdvanceAmount', editingCell.AdvanceAmount),
        BonusAmount: getValue('BonusAmount', editingCell.BonusAmount),
        TotalAmount: getValue('TotalAmount', editingCell.TotalAmount),
        OfficialAmount: getValue('OfficialAmount', editingCell.OfficialAmount),
        // filterMonth 1-based; UTC, чтобы месяц не зависел от часового пояса браузера
        Timestamp: new Date(
          Date.UTC(filterYear, filterMonth - 1, 1)
        ).toISOString(),
        Comment: comment || null,
      };

      await updateEmployeeSalary({ ActiveBranchId, data }).unwrap();

      showNotification(SUCCESS_MESSAGE, SUCCESS);
      closeEditCellModal();
      fetchData();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    ActiveBranchId,
    closeEditCellModal,
    editableField,
    editableValue,
    editingCell,
    fetchData,
    shownPeriod,
    showNotification,
    updateEmployeeSalary,
  ]);

  const handleRevokeConfirm = useCallback((userId: number) => {
    setIsRevokeConfirm(true);
    setIsConfirmModalOpen(true);
    setSelectedEmployeeId(userId);
  }, []);

  return {
    handleReset,
    form,
    handleSubmitForm,
    onConfirmSalary,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    selectedEmployeeId,
    handleEditableCellClick,
    editCellModalOpen,
    closeEditCellModal,
    editableValue,
    handleEditableValueChange,
    editableField,
    setEditableValue,
    editingCell,
    handleSaveEditCellModal,
    isUpdateEmployeeSalaryLoading,
    isLoading,
    tableData,
    fetchData,
    handleSearchData,
    shownPeriod,
    isRevokeConfirm,
    handleRevokeConfirm,
    hasUserRightToConfirmSalary,
    hasUserRightToEditBefore,
    hasUserRightToEditAfterConfirm,
  };
};
